import React, { useMemo, useState } from "react";
import {
  useGame,
  useMyEntitlements,
  usePublisherFitForGame,
  usePublisherGameRolePlay,
  useRatePublisherRolePlay,
} from "@seabrookstudios/pitch2table";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { LockedButton, LockedListItem } from "../core/HasEntitlements";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CardActions from "@mui/material/CardActions";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { FullScreenDialog } from "../core/FullScreenDialog";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Warning from "@mui/icons-material/Warning";
import Markdown from "react-markdown";
import { s2UserId } from "@seabrookstudios/types";
import { TierLabels } from "@seabrookstudios/pitch2table-core";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * @param {object} props
 * @param {string} props.title
 * @param {boolean|undefined} props.isComplete
 */
const RequirementListItem = ({ title, isComplete }) => {
  return (
    <ListItem>
      <ListItemText primary={title} />
      <ListItemIcon>
        <Chip
          icon={isComplete ? <CheckCircle /> : <Warning />}
          label={isComplete ? "Done" : "Missing"}
          color={isComplete ? "success" : "warning"}
        />
      </ListItemIcon>
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} props.publisherGameId
 */
const RolePlayFeedbackCard = ({ publisherGameId }) => {
  const [rated, setRated] = useState(false);
  const { mutate: rateRolePlay, isPending } = useRatePublisherRolePlay(publisherGameId);

  /**
   * @param {boolean} rating
   */
  const handleRate = (rating) => {
    rateRolePlay(rating);
    setRated(true);
  };

  return (
    <Box pt={1}>
      {rated || isPending ? (
        <Typography>Thank you for your feedback!</Typography>
      ) : (
        <>
          <Typography>Did you find this simulation useful?</Typography>
          <Box display="flex" gap={1}>
            <Button variant="outlined" onClick={() => handleRate(false)} color="error">
              No
            </Button>
            <Button variant="contained" onClick={() => handleRate(true)}>
              Yes
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView} props.publisherGame
 */
const RolePlaySubmissionResponse = ({ publisherGame }) => {
  const { data: rolePlay, isPending, isError } = usePublisherGameRolePlay(publisherGame.id);

  if (isPending) {
    return <CircularProgress />;
  }

  if (isError) {
    return <Alert severity="error">An error occurred while fetching the simulation response.</Alert>;
  }

  return (
    <Box pt={2}>
      <Markdown>{rolePlay || "The publisher response will be displayed here."}</Markdown>
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        This simulation is powered by AI and may not be accurate. This feature is meant to give you insights, not
        answers. When in doubt, pitch to the publisher.
      </Typography>
      {rolePlay && <RolePlayFeedbackCard publisherGameId={publisherGame.id} />}
    </Box>
  );
};

const Ryan = s2UserId("73ee6e6e-5ca0-4271-ac2e-f9b2fed27fd7");
const Ravensburger = "40321209-fbf7-429b-a1e8-9a626af34ac4";

/**
 * @param {object} props
 * @param {import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView} props.publisherGame
 */
const RolePlaySubmissionButton = ({ publisherGame }) => {
  const { data: game } = useGame(publisherGame.gameId);
  const { data: entitlements } = useMyEntitlements();
  const [isOpen, setIsOpen] = useState(false);

  const rolePlayButtonDisabled = publisherGame.publisherId !== Ravensburger && entitlements?.id !== Ryan;

  const CustomControl = () => <Button variant="contained">Explain</Button>;
  return (
    <FullScreenDialog title="Simulate Role Play Submission" CustomControl={<CustomControl />}>
      <Alert severity="info">
        Simulate a publisher reviewing your game. At pitch2table, we take your game, our PublisherDNA profile and
        attempt to simulate the publisher&apos;s response to your game submission.
        <br />
        <br />
        Use this feature to make sure you&apos;ve considered all angles in your pitch.
        <br />
        <br />
        The publisher simulation requires sending information about your game to a generative AI to simulate the
        publisher&apos;s response. Your game data is never used to train models, AI and is never shared with any third
        parties.
      </Alert>
      <Box pt={2}>
        <Typography>
          The submission simulation will be more useful with more complete information. Try to ensure the following is
          uploaded against the game in pitch2table.
        </Typography>
      </Box>
      <List>
        <RequirementListItem title="Tagline" isComplete={!!game?.tag} />
        <RequirementListItem title="Pitch" isComplete={!!game?.description} />
        <RequirementListItem title="Tags" isComplete={!!game?.tags} />
        <RequirementListItem title="Latest Rules" isComplete={!!game?.rules} />
        <RequirementListItem title="Latest Sell Sheet" isComplete={!!game?.sellsheet} />
      </List>
      {isOpen ? (
        <RolePlaySubmissionResponse publisherGame={publisherGame} />
      ) : (
        <LockedButton lockedLabel="Run" variant="contained">
          <Button variant="contained" onClick={() => setIsOpen(true)} disabled={rolePlayButtonDisabled}>
            Run
          </Button>
        </LockedButton>
      )}
    </FullScreenDialog>
  );
};

/**
 * @param {number} score
 * @returns {number|null}
 */
const toDisplayPercent = (score) => {
  if (typeof score !== "number" || Number.isNaN(score)) {
    return null;
  }
  if (score >= 0 && score <= 1) {
    return Math.round(score * 100);
  }
  return Math.round(Math.min(100, Math.max(0, score)));
};

/**
 * @param {object} props
 * @param {import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView} props.publisherGame
 * @param {boolean} [props.startOpen]
 */
export const PublisherFitAnalysisCard = ({ publisherGame, startOpen = true }) => {
  const { data: entitlements } = useMyEntitlements();
  const { gameId, publisherId } = publisherGame;

  const { data: fitScores = [], isPending, isError } = usePublisherFitForGame(gameId);

  const entry = useMemo(() => fitScores.find((f) => f.publisherId === publisherId), [fitScores, publisherId]);

  const percent = entry ? toDisplayPercent(entry.score) : null;

  if (!entitlements) {
    return (
      <Card>
        <CardContent>
          <LockedListItem title="Publisher Fit Analysis" />
        </CardContent>
      </Card>
    );
  }

  if (!entitlements.hasSKU0002) {
    return (
      <Card>
        <LockedListItem title="Publisher Fit Analysis" />
      </Card>
    );
  }

  return (
    <Accordion defaultExpanded={startOpen}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <CardHeader
          title="Publisher Fit Analysis"
          subheader="How well this publisher matches your game, based on your pitch and their catalogue."
        />
      </AccordionSummary>
      <AccordionDetails>
        <CardContent>
          <Alert severity="info">
            <Typography>
              Publisher Fit Analysis is an experimental tool that matches your pitch against the PublisherDNA profile.
              Because we match against a publisher&apos;s entire brand essence rather than a single game, a perfect
              score is rare. Always verify a publisher&apos;s current status and specific submission guidelines on their
              official website before reaching out.
            </Typography>
          </Alert>
          {isPending && (
            <Box p={1}>
              <CircularProgress />
            </Box>
          )}
          {!isPending && isError && (
            <Typography>An error occurred while fetching the publisher fit analysis.</Typography>
          )}
          {!isPending && !isError && entry === undefined && (
            <Typography>The publisher fit analysis is not available.</Typography>
          )}
          {!isPending && !isError && entry !== undefined && percent !== null && (
            <Typography variant="h3">
              {TierLabels[entry.label]} - {percent}%
            </Typography>
          )}
          {!isPending && !isError && entry !== undefined && percent === null && (
            <Typography>The publisher fit analysis is not available.</Typography>
          )}
        </CardContent>
        <CardActions>
          <RolePlaySubmissionButton publisherGame={publisherGame} />
        </CardActions>
      </AccordionDetails>
    </Accordion>
  );
};
